import React from "react";
import { useParams, Link } from "react-router-dom";
import { Box, Typography, Grid, Button, CircularProgress, Chip, TextField } from "@mui/material";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { ArrowLeft, CalendarDays, MapPin } from "lucide-react";
import { getResource } from "../api/resources";
import { registerEvent } from "../api/events";
import PageShell from "../components/layout/PageShell";
import { resolveMediaUrl } from "../utils/mediaUrl";

export default function ResourceDetailPage() {
  const { slug } = useParams();
  const { register, handleSubmit, reset } = useForm();

  const { data: resource, isLoading, isError } = useQuery({
    queryKey: ["resource", slug],
    queryFn: () => getResource(slug).then(r => r.data),
    enabled: !!slug,
  });

  const event = resource?.event_details;

  const mutation = useMutation({
    mutationFn: (data) => registerEvent(event.id, data),
    onSuccess: () => { toast.success("Registered successfully!"); reset(); },
    onError: () => toast.error("Registration failed. Please try again."),
  });

  if (isLoading) return (
    <PageShell>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    </PageShell>
  );

  if (isError || !resource) return (
    <PageShell>
      <Box textAlign="center" py={20}>
        <Typography variant="h4" gutterBottom>Resource not found</Typography>
        <Button component={Link} to="/resources" startIcon={<ArrowLeft size={16} />}>
          Back to Resources
        </Button>
      </Box>
    </PageShell>
  );

  // Some detail-endpoint responses return "thumbnail" (absolute) and
  // others return "thumbnail_url" (sometimes relative). Prefer whichever
  // is present, then normalize to an absolute URL.
  const thumbnailSrc = resolveMediaUrl(resource.thumbnail_url || resource.thumbnail);

  return (
    <PageShell>
      <Box sx={{
        background: "linear-gradient(135deg, #0A0F1E 0%, #0f2044 100%)",
        pt: 16, pb: 10, position: "relative", overflow: "hidden",
      }}>
        <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, position: "relative" }}>
          <Button
            component={Link} to="/resources"
            startIcon={<ArrowLeft size={16} />}
            sx={{ color: "rgba(255,255,255,0.6)", mb: 3, "&:hover": { color: "white" } }}
          >
            Back to Resources
          </Button>
          {resource.category_name && (
            <Chip label={resource.category_name} color="primary" size="small" sx={{ mb: 2 }} />
          )}
          <Typography variant="h2" fontWeight={700} color="white"
            sx={{ color: 'white', fontSize: { xs: "1.8rem", md: "2.6rem" }, lineHeight: 1.2, mb: 2 }}>
            {resource.title}
          </Typography>
          {resource.excerpt && (
            <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.70)", fontWeight: 400, maxWidth: 700, textAlign: "justify" }}>
              {resource.excerpt}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 3, mt: 3, flexWrap: "wrap" }}>
            {resource.author_name && (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)" }}>
                By {resource.author_name}
              </Typography>
            )}
            {resource.publish_date && (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)" }}>
                {dayjs(resource.publish_date).format("DD MMM YYYY")}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, py: 8 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} lg={event ? 7 : 12}>
            {thumbnailSrc && (
              <Box
                component="img"
                src={thumbnailSrc}
                alt={resource.title}
                sx={{ width: "100%", borderRadius: 3, mb: 4 }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            )}
            {resource.body && (
              <Typography variant="body1" color="text.secondary" lineHeight={1.9}
                sx={{ whiteSpace: "pre-line", textAlign: "justify" }}>
                {resource.body}
              </Typography>
            )}
          </Grid>

          {event && (
            <Grid item xs={12} lg={5}>
              <Box sx={{ position: "sticky", top: 100, bgcolor: "#F8FAFC", borderRadius: 3, p: 3.5 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>Register for this event</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, color: "text.secondary" }}>
                  <CalendarDays size={16} />
                  <Typography variant="body2">
                    {dayjs(event.start_datetime).format("DD MMM YYYY, h:mm A")}
                  </Typography>
                </Box>
                {event.location && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5, color: "text.secondary" }}>
                    <MapPin size={16} />
                    <Typography variant="body2">{event.location}</Typography>
                  </Box>
                )}

                {!event.is_registration_open ? (
                  <Typography color="error" fontWeight={600}>Registration closed</Typography>
                ) : (
                  <Box component="form" onSubmit={handleSubmit((d) => mutation.mutate(d))}
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <TextField label="Name" fullWidth size="small" {...register("name", { required: true })} />
                    <TextField label="Email" fullWidth size="small" {...register("email", { required: true })} />
                    <TextField label="Phone" fullWidth size="small" {...register("phone")} />
                    <TextField label="Company" fullWidth size="small" {...register("company")} />
                    <Button type="submit" variant="contained" fullWidth size="large"
                      disabled={mutation.isPending} sx={{ mt: 1, borderRadius: 2 }}>
                      {mutation.isPending ? "Registering..." : "Register Now"}
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </PageShell>
  );
}